import { Component, computed, inject, model } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
    Button,
    Input,
    ModalAdapter,
    ModalContainer,
    ModalHeader,
    Select,
    ToastService,
} from "@malirang/front-share";
import {
    getEditorText,
    SmartEditorComponent,
} from "../../../../../components/smart-editor/smart-editor.component";
import {
    CreateDocumentTemplateDto,
    DocumentTemplateCategoryService,
    DocumentTemplateService,
} from "@malirang/api-client";
import { rxResource } from "@angular/core/rxjs-interop";
import { of } from "rxjs";
import { toObservableSignal } from "ngxtension/to-observable-signal";
import {
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from "@angular/forms";
import { CenterStore } from "../../../../../stores/center/center.store";

export type AddDocumentTemplateModalProps = {
    id?: string;
};

@Component({
    selector: "app-add-document-template",
    templateUrl: "./add-document-template.modal.html",
    styleUrls: ["./add-document-template.modal.css"],
    imports: [
        CommonModule,
        ModalHeader,
        ModalContainer,
        Input,
        Select,
        SmartEditorComponent,
        Button,
        ReactiveFormsModule,
    ],
})
export class AddDocumentTemplateModal extends ModalAdapter<AddDocumentTemplateModalProps> {
    readonly centerStore = inject(CenterStore);
    readonly documentTemplateId = computed(() => this.props()?.id);
    readonly documentTemplateService = inject(DocumentTemplateService);
    readonly documentTemplateCategoryService = inject(
        DocumentTemplateCategoryService,
    );

    documentTemplate$ = rxResource({
        stream: () =>
            this.documentTemplateId()
                ? this.documentTemplateService.documentTemplateControllerGetDocumentById(
                      {
                          id: this.documentTemplateId()!,
                      },
                  )
                : of(null),
    });
    documentTemplate = toObservableSignal(this.documentTemplate$.value);

    $categories = rxResource({
        stream: () =>
            this.documentTemplateCategoryService.documentTemplateCategoryControllerFindAll(
                {
                    centerId: this.centerStore.currentCenterId(),
                },
            ),
    });
    categories = this.$categories.value;

    form = new FormGroup({
        name: new FormControl<string>("", [Validators.required]),
        categoryId: new FormControl<string>("", [Validators.required]),
        content: new FormControl<string>(""),
    });

    oEditors = model<any>();

    constructor() {
        super();

        this.documentTemplate.subscribe((template) => {
            if (template) {
                this.form.patchValue({
                    name: template.name,
                    categoryId: template.category.id,
                });
            } else {
                this.form.reset();
            }
        });
    }

    submit() {
        const content = getEditorText(this.oEditors());

        if (!content) {
            this.toastService.error("문서 내용을 입력해주세요.");
            return;
        }

        const body: CreateDocumentTemplateDto = {
            name: this.form.value.name ?? "",
            categoryId: this.form.value.categoryId ?? "",
            content,
        };

        if (this.documentTemplateId()) {
            this.documentTemplateService
                .documentTemplateControllerUpdateDocument({
                    id: this.documentTemplateId()!,
                    body: body as CreateDocumentTemplateDto,
                    centerId: this.centerStore.currentCenterId(),
                })
                .subscribe({
                    next: () => {
                        this.toastService.success("문서가 수정되었습니다.");
                        this.close("success");
                    },
                    error: (err) => {
                        this.toastService.error(err.message);
                    },
                });
        } else {
            this.documentTemplateService
                .documentTemplateControllerCreateDocument({
                    body,
                    centerId: this.centerStore.currentCenterId(),
                })
                .subscribe({
                    next: () => {
                        this.toastService.success("문서가 생성되었습니다.");
                        this.close("success");
                    },
                });
        }
    }
}
